/**
 * Types pour le système de progression de chargement
 *
 * Ce système permet aux pages de reporter leur progression de chargement
 * pour afficher une barre de progression précise à l'utilisateur.
 *
 * @example
 * ```tsx
 * // Définir les étapes de chargement d'une page
 * const VIEWER_STEPS: LoadingStep[] = [
 *   { id: 'init', label: 'Initialisation...', weight: 20 },
 *   { id: 'cesium', label: 'Chargement Cesium...', weight: 40 },
 * ];
 * ```
 */

/**
 * Représente une étape de chargement
 */
export interface LoadingStep {
  /** Identifiant unique de l'étape */
  id: string;
  /** Label affiché à l'utilisateur */
  label: string;
  /** Poids relatif de l'étape (pour calculer le pourcentage) */
  weight: number;
}

/**
 * État d'une étape de chargement
 */
export type StepStatus = "pending" | "in-progress" | "completed" | "error";

/**
 * État interne d'une étape avec son statut
 */
export interface LoadingStepState extends LoadingStep {
  status: StepStatus;
  error?: string;
}

/**
 * État global du système de chargement
 */
export interface LoadingState {
  /** Étapes définies pour la page courante */
  steps: LoadingStepState[];
  /** Pourcentage de progression (0-100) */
  progress: number;
  /** Label de l'étape en cours */
  currentLabel: string;
  /** Indique si le chargement est terminé */
  isComplete: boolean;
  /** Indique si une erreur est survenue */
  hasError: boolean;
  /** Message d'erreur si applicable */
  errorMessage?: string;
}

/**
 * Actions disponibles pour les pages enfants
 */
export interface LoadingActions {
  /**
   * Initialise les étapes de chargement pour une page
   * ⚠️ OBLIGATOIRE : Doit être appelé au montage de la page
   */
  initSteps: (steps: LoadingStep[]) => void;

  /**
   * Démarre une étape spécifique
   * @param stepId - L'ID de l'étape à démarrer
   */
  startStep: (stepId: string) => void;

  /**
   * Marque une étape comme complétée et passe à la suivante
   * @param stepId - L'ID de l'étape complétée
   */
  completeStep: (stepId: string) => void;

  /**
   * Signale une erreur sur une étape
   * @param stepId - L'ID de l'étape en erreur
   * @param message - Message d'erreur
   */
  failStep: (stepId: string, message: string) => void;

  /**
   * Réinitialise le système de chargement
   */
  reset: () => void;
}

/**
 * Contexte complet exposé aux composants
 */
export interface LoadingContextValue extends LoadingState, LoadingActions {}

/**
 * Configuration du système de chargement
 */
export interface LoadingConfig {
  /** Activer les logs de debug en console */
  enableDebugLogs: boolean;
  /** Timeout avant d'afficher un warning si une étape est bloquée (ms) */
  stuckWarningTimeout: number;
  /** Préfixe pour les logs console */
  logPrefix: string;
}

/**
 * Constantes par défaut
 */
export const DEFAULT_LOADING_CONFIG: LoadingConfig = {
  enableDebugLogs: import.meta.env.DEV,
  stuckWarningTimeout: 10000, // 10 secondes
  logPrefix: "[LoadingProgress]",
};
